import DownloadOptionsButton from '../../../components/action_button/DownloadOptionsButton'
import { runFileDownload } from '../../../components/downloads/fileDownloadStore'
import { buildTimestampControlPdf } from '../helpers/downloads/timestampControlPdfBuilder'
import { buildTimestampControlCsvRows } from '../helpers/downloads/timestampControlCsvBuilder'
import { buildActivityBreakdownPdf } from '../helpers/downloads/activityBreakdownPdfBuilder'
import { buildActivityBreakdownCsvRows } from '../helpers/downloads/activityBreakdownCsvBuilder'
import { useState } from 'react'
import { Box } from '@mui/material'
import { getCurrentDate } from '../../../helpers/formatDate'
import cookie from 'react-cookies'
import {
  buildRouteUserReportCsvRows,
  downloadCsvRows,
  exportRouteUserReportPDF
} from '../helpers/downloads/exportRouteUserReportFiles'

const UserReportDownloadComponent = ({
  activeTab,
  startDate,
  endDate,
  salesmanName,
  formatActivityName,
  onLoadDownloadData,
  isDownloadFetching = false,
  isDataUpdating = false,
  isTimestampSameDay,
  timestampSummary = {},
  routeCartData,
  geofenceSegment = 'general',
  salesmanCode,
  business,
  activityOverview,
  activityEffectivenessSegment = 'general'
}) => {
  const supervisorName = cookie.load('user_name')
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadLabel, setDownloadLabel] = useState('')

  const usernameWithoutSpaces = salesmanName?.replace(/\s/g, '') || 'usuario'

  const handleRouteDownload = async (type, signal) => {
    setIsDownloading(true)
    setDownloadLabel('Descargando...')

    try {
      const downloadData = await onLoadDownloadData('route_execution', { signal })
      signal?.throwIfAborted()
      const routeExecutionDownloadData = downloadData?.results || []
      if (!routeExecutionDownloadData.length) return { empty: true }
      const activityDownloadData = downloadData?.activityDetails || []
      const timestampDownloadData = downloadData?.timestampDetails || []
      const exportBusiness = downloadData?.business || business
      const businessName = exportBusiness?.name || 'negocio'

      if (type === 'pdf') {
        const filename = exportRouteUserReportPDF({
          routeCartData,
          geofenceSegment,
          salesmanCode,
          business: exportBusiness,
          routeExecutionDownloadData,
          activityDownloadData,
          timestampDownloadData,
          salesmanName,
          startDate,
          endDate,
          formatActivityName
        })
        return { filename }
      } else {
        const rows = buildRouteUserReportCsvRows({
          routeCartData,
          geofenceSegment,
          salesmanCode,
          business: exportBusiness,
          routeExecutionDownloadData,
          activityDownloadData,
          timestampDownloadData,
          formatActivityName,
          salesmanName,
          supervisorName,
          startDate,
          endDate
        })

        const filename = `Reporte_EjecucionPlanDeVisitas_${usernameWithoutSpaces}_${businessName}_${getCurrentDate()}.csv`
        downloadCsvRows(rows, filename)
        return { filename }
      }
    } catch (error) {
      console.error('Route download error:', error)
      throw error
    } finally {
      setIsDownloading(false)
      setDownloadLabel('')
    }
  }

  const handleSingleTabDownload = async (type, signal) => {
    const isActivityTab = activeTab === 'activity_breakdown'

    setIsDownloading(true)
    setDownloadLabel('Descargando...')

    try {
      const downloadData = await onLoadDownloadData(activeTab, { signal })
      signal?.throwIfAborted()
      const results = downloadData?.results || []
      if (!results.length) return { empty: true }
      const exportBusiness = downloadData?.business || business
      const businessName = exportBusiness?.name || 'negocio'
      const filenameBase = isActivityTab
        ? `DesgloseDeActividades_${usernameWithoutSpaces}_${businessName}_${getCurrentDate()}`
        : `ControlDeHorario_${usernameWithoutSpaces}_${businessName}_${getCurrentDate()}`
      if (isActivityTab) {
        const options = {
          results,
          overview: activityOverview,
          effectivenessSegment: activityEffectivenessSegment,
          formatActivityName,
          startDate,
          endDate,
          salesmanName,
          salesmanCode,
          business: exportBusiness
        }
        if (type === 'pdf') buildActivityBreakdownPdf(options).save(`${filenameBase}.pdf`)
        else downloadCsvRows(buildActivityBreakdownCsvRows(options), `${filenameBase}.csv`)
        return { filename: `${filenameBase}.${type}` }
      }

      const options = {
        results,
        timestampSummary,
        isTimestampSameDay,
        startDate,
        endDate,
        salesmanName,
        salesmanCode,
        business: exportBusiness
      }
      if (type === 'pdf') buildTimestampControlPdf(options).save(`${filenameBase}.pdf`)
      else downloadCsvRows(buildTimestampControlCsvRows(options), `${filenameBase}.csv`)
      return { filename: `${filenameBase}.${type}` }
    } catch (error) {
      console.error('User report download error:', error)
      throw error
    } finally {
      setIsDownloading(false)
      setDownloadLabel('')
    }
  }

  const handleDownload = (type) => {
    if (isDataUpdating || isDownloadFetching || isDownloading) return
    return runFileDownload({
      title: `Reporte de ${salesmanName || 'usuario'} en ${type.toUpperCase()}`,
      task: async (signal) => {
        if (activeTab === 'route_execution') return handleRouteDownload(type, signal)
        else return handleSingleTabDownload(type, signal)
      }
    })
  }

  return (
    <Box>
      <DownloadOptionsButton
        label={isDataUpdating ? 'Actualizando...' : downloadLabel || 'Descargar'}
        disabled={isDownloading || isDownloadFetching || isDataUpdating}
        onPrimaryClick={() => handleDownload('pdf')}
        items={[
          {
            label: 'Reporte en PDF',
            description: 'Indicadores y detalle del periodo.',
            onClick: () => handleDownload('pdf')
          },
          {
            label: 'Reporte en CSV',
            description: 'Todos los registros del reporte.',
            onClick: () => handleDownload('csv')
          }
        ]}
      />
    </Box>
  )
}

export default UserReportDownloadComponent
